import type { SupportedLocale } from '@erp/shared';

export const stockAdjustmentTranslations = {
  de: {
    newBatch: '+ Neue Charge',
    more: 'Weitere Angaben',
    status: 'Status',
    bestBefore: 'MHD',
    receivedAt: 'Einlagerungsdatum',
    expiryDate: 'Verfallsdatum',
    empty: 'Für diesen Artikel ist noch keine Charge vorhanden. Lege eine neue Charge an.',
    loadError: 'Die Chargenliste konnte nicht geladen werden.',
    fieldsError: 'Die Chargen-Zusatzfelder konnten nicht geladen werden.',
    countedBatch: 'Gezählter Bestand der Charge',
    countedBatchHint:
      'Die gezählte Menge gilt nur für die gewählte Charge. Andere Chargen bleiben unverändert.',
    preview: 'Charge: {{batch}} | Bisher: {{before}} | Korrektur: {{delta}} | Danach: {{after}}',
    totalAfter: 'Artikelgesamtbestand danach: {{total}}',
    previewHint:
      'Die Buchung berechnet die Differenz gegen den aktuellen Bestand zum Buchungszeitpunkt.',
    discrepancy:
      'Bestandsabweichung: Artikel {{stock}}, Summe aller Chargen {{batches}}, Differenz {{difference}}. Eine Korrektur ordnet vorhandene Mengen nicht zu. Prüfe das Lagerjournal; die Zuordnung muss separat geklärt werden. Bestehende Buchungen bleiben erhalten.',
    batchRequired: 'Wähle eine vorhandene Charge oder lege eine neue an.',
    chooseOneBatch: 'Wähle entweder eine bestehende oder eine neue Charge.',
    newBatchPositive:
      'Eine neue Charge benötigt eine positive Menge. Für Abgänge wähle eine bestehende Charge.',
    zero: 'Keine Bestandsänderung: Es wird weder eine Charge noch eine Buchung angelegt.',
    insufficient: 'Der Bestand reicht für diese Korrektur nicht aus.',
    blocked: 'Aus gesperrten oder abgelaufenen Chargen darf kein Bestand entnommen werden.',
    mhdRequired: 'Für diesen Artikel ist ein Mindesthaltbarkeitsdatum erforderlich.',
    duplicateBatch:
      'Diese Chargennummer existiert bereits für den Artikel. Wähle die vorhandene Charge aus.',
    retryConflict:
      'Dieser Buchungsauftrag wurde bereits mit anderen Angaben gespeichert. Aktualisiere den Artikel und prüfe das Lagerjournal.',
    invalid: 'Bitte korrigiere die markierten Angaben.',
    discard: 'Eingaben verwerfen und Bestandskorrektur schließen?',
    help: 'Hilfe zur Bestandskorrektur',
  },
  en: {
    newBatch: '+ New batch',
    more: 'More details',
    status: 'Status',
    bestBefore: 'Best before',
    receivedAt: 'Received date',
    expiryDate: 'Expiry date',
    empty: 'There is no batch for this product yet. Create a new batch.',
    loadError: 'The batch list could not be loaded.',
    fieldsError: 'Batch custom fields could not be loaded.',
    countedBatch: 'Counted stock of the batch',
    countedBatchHint:
      'The counted quantity applies only to the selected batch. Other batches remain unchanged.',
    preview: 'Batch: {{batch}} | Before: {{before}} | Adjustment: {{delta}} | After: {{after}}',
    totalAfter: 'Total product stock afterwards: {{total}}',
    previewHint: 'Posting calculates the difference against current stock at the time of posting.',
    discrepancy:
      'Stock discrepancy: product {{stock}}, sum of all batches {{batches}}, difference {{difference}}. An adjustment does not allocate existing quantities. Check the stock journal; allocation must be resolved separately. Existing postings are retained.',
    batchRequired: 'Select an existing batch or create a new one.',
    chooseOneBatch: 'Choose either an existing or a new batch.',
    newBatchPositive:
      'A new batch requires a positive quantity. Select an existing batch for withdrawals.',
    zero: 'No stock change: neither a batch nor a posting will be created.',
    insufficient: 'There is insufficient stock for this adjustment.',
    blocked: 'Stock cannot be withdrawn from blocked or expired batches.',
    mhdRequired: 'This product requires a best-before date.',
    duplicateBatch: 'This batch number already exists for this product. Select the existing batch.',
    retryConflict:
      'This posting request was already saved with different details. Refresh the product and check the stock journal.',
    invalid: 'Please correct the highlighted fields.',
    discard: 'Discard your entries and close the stock adjustment?',
    help: 'Stock adjustment help',
  },
  fr: {
    newBatch: '+ Nouveau lot',
    more: 'Autres informations',
    status: 'Statut',
    bestBefore: 'DDM',
    receivedAt: 'Date de réception',
    expiryDate: 'Date de péremption',
    empty: 'Aucun lot n’existe encore pour cet article. Créez un nouveau lot.',
    loadError: 'La liste des lots n’a pas pu être chargée.',
    fieldsError: 'Les champs personnalisés des lots n’ont pas pu être chargés.',
    countedBatch: 'Stock compté du lot',
    countedBatchHint:
      'La quantité comptée concerne uniquement le lot sélectionné. Les autres lots restent inchangés.',
    preview: 'Lot : {{batch}} | Avant : {{before}} | Correction : {{delta}} | Après : {{after}}',
    totalAfter: 'Stock total de l’article après correction : {{total}}',
    previewHint:
      'La comptabilisation calcule la différence par rapport au stock actuel au moment de l’opération.',
    discrepancy:
      'Écart de stock : article {{stock}}, somme de tous les lots {{batches}}, différence {{difference}}. Une correction n’affecte pas les quantités existantes à un lot. Vérifiez le journal de stock ; l’affectation doit être clarifiée séparément. Les écritures existantes sont conservées.',
    batchRequired: 'Sélectionnez un lot existant ou créez-en un.',
    chooseOneBatch: 'Choisissez un lot existant ou un nouveau lot.',
    newBatchPositive:
      'Un nouveau lot nécessite une quantité positive. Pour les sorties, sélectionnez un lot existant.',
    zero: 'Aucun changement de stock : aucun lot ni aucune écriture ne sera créé.',
    insufficient: 'Le stock est insuffisant pour cette correction.',
    blocked: 'Les sorties de lots bloqués ou périmés sont interdites.',
    mhdRequired: 'Une date de durabilité minimale est requise pour cet article.',
    duplicateBatch: 'Ce numéro de lot existe déjà pour cet article. Sélectionnez le lot existant.',
    retryConflict:
      'Cette demande a déjà été enregistrée avec d’autres informations. Actualisez l’article et vérifiez le journal de stock.',
    invalid: 'Corrigez les champs signalés.',
    discard: 'Abandonner les saisies et fermer la correction de stock ?',
    help: 'Aide à la correction de stock',
  },
  es: {
    newBatch: '+ Nuevo lote',
    more: 'Más datos',
    status: 'Estado',
    bestBefore: 'Consumo preferente',
    receivedAt: 'Fecha de recepción',
    expiryDate: 'Fecha de caducidad',
    empty: 'Este artículo aún no tiene ningún lote. Crea un lote nuevo.',
    loadError: 'No se pudo cargar la lista de lotes.',
    fieldsError: 'No se pudieron cargar los campos personalizados del lote.',
    countedBatch: 'Existencias contadas del lote',
    countedBatchHint:
      'La cantidad contada corresponde solo al lote seleccionado. Los demás lotes no cambian.',
    preview: 'Lote: {{batch}} | Antes: {{before}} | Corrección: {{delta}} | Después: {{after}}',
    totalAfter: 'Existencias totales del artículo después: {{total}}',
    previewHint:
      'Al contabilizar se calcula la diferencia con las existencias actuales en ese momento.',
    discrepancy:
      'Discrepancia: artículo {{stock}}, suma de todos los lotes {{batches}}, diferencia {{difference}}. Una corrección no asigna cantidades existentes. Revisa el diario de existencias; la asignación debe aclararse por separado. Los asientos existentes se conservan.',
    batchRequired: 'Selecciona un lote existente o crea uno nuevo.',
    chooseOneBatch: 'Elige un lote existente o uno nuevo.',
    newBatchPositive:
      'Un lote nuevo requiere una cantidad positiva. Para salidas, selecciona un lote existente.',
    zero: 'Sin cambio de existencias: no se creará ningún lote ni asiento.',
    insufficient: 'No hay existencias suficientes para esta corrección.',
    blocked: 'No se permiten salidas de lotes bloqueados o caducados.',
    mhdRequired: 'Este artículo requiere una fecha de consumo preferente.',
    duplicateBatch: 'Este número de lote ya existe para el artículo. Selecciona el lote existente.',
    retryConflict:
      'Esta solicitud ya se guardó con otros datos. Actualiza el artículo y revisa el diario de existencias.',
    invalid: 'Corrige los campos señalados.',
    discard: '¿Descartar los datos y cerrar la corrección de existencias?',
    help: 'Ayuda de corrección de existencias',
  },
  tr: {
    newBatch: '+ Yeni parti',
    more: 'Diğer bilgiler',
    status: 'Durum',
    bestBefore: 'Tavsiye edilen tüketim tarihi',
    receivedAt: 'Giriş tarihi',
    expiryDate: 'Son kullanma tarihi',
    empty: 'Bu ürün için henüz parti yok. Yeni bir parti oluşturun.',
    loadError: 'Parti listesi yüklenemedi.',
    fieldsError: 'Parti ek alanları yüklenemedi.',
    countedBatch: 'Partinin sayılan stoğu',
    countedBatchHint: 'Sayılan miktar yalnızca seçilen partiye uygulanır. Diğer partiler değişmez.',
    preview: 'Parti: {{batch}} | Önce: {{before}} | Düzeltme: {{delta}} | Sonra: {{after}}',
    totalAfter: 'İşlem sonrası toplam ürün stoğu: {{total}}',
    previewHint: 'Kayıt sırasında fark, o andaki güncel stok üzerinden hesaplanır.',
    discrepancy:
      'Stok farkı: ürün {{stock}}, tüm partilerin toplamı {{batches}}, fark {{difference}}. Düzeltme mevcut miktarları partilere atamaz. Stok günlüğünü kontrol edin; atama ayrıca açıklığa kavuşturulmalıdır. Mevcut kayıtlar korunur.',
    batchRequired: 'Mevcut bir parti seçin veya yeni bir parti oluşturun.',
    chooseOneBatch: 'Mevcut bir parti ya da yeni bir parti seçin.',
    newBatchPositive:
      'Yeni parti için pozitif miktar gerekir. Çıkışlar için mevcut bir parti seçin.',
    zero: 'Stok değişikliği yok: parti veya stok hareketi oluşturulmaz.',
    insufficient: 'Bu düzeltme için stok yetersiz.',
    blocked: 'Bloke edilmiş veya süresi dolmuş partilerden stok çıkışı yapılamaz.',
    mhdRequired: 'Bu ürün için tavsiye edilen tüketim tarihi gereklidir.',
    duplicateBatch: 'Bu parti numarası ürün için zaten mevcut. Mevcut partiyi seçin.',
    retryConflict:
      'Bu kayıt isteği farklı bilgilerle zaten kaydedildi. Ürünü yenileyip stok günlüğünü kontrol edin.',
    invalid: 'İşaretli alanları düzeltin.',
    discard: 'Girilen bilgiler silinip stok düzeltmesi kapatılsın mı?',
    help: 'Stok düzeltmesi yardımı',
  },
} as const satisfies Record<SupportedLocale, Record<string, string>>;
